import { useState, useEffect, useCallback } from 'react';
import {
  getStoredIdentity,
  createIdentity,
  importIdentity,
  isProtectionEnabled,
  enableProtection as enableProtectionImpl,
  disableProtection as disableProtectionImpl,
  unlock as unlockImpl,
  lock as lockImpl,
} from '../utils/identity';
import { getUnlockedPrivateKey } from '../utils/sessionUnlockedKey';
import type { LocalIdentity } from '../types/identity';

function mergeIdentityWithSession(
  stored: LocalIdentity | null,
): LocalIdentity | null {
  if (stored === null) {
    return null;
  }
  if (stored.privateKey !== undefined) {
    return stored;
  }
  const sessionKey = getUnlockedPrivateKey();
  if (sessionKey === null) {
    return stored;
  }
  return { ...stored, privateKey: sessionKey };
}

export function useIdentity() {
  const [identity, setIdentity] = useState<LocalIdentity | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshIdentity = useCallback((): void => {
    const stored = getStoredIdentity();
    setIdentity(mergeIdentityWithSession(stored));
  }, []);

  useEffect(() => {
    refreshIdentity();
    setIsLoading(false);
  }, [refreshIdentity]);

  const createNewIdentity = (name?: string): LocalIdentity => {
    const newIdentity = createIdentity(name);
    setIdentity(newIdentity);
    return newIdentity;
  };

  const importExistingIdentity = (
    seedOrPrivateKey: string,
    name?: string,
  ): LocalIdentity | null => {
    const imported = importIdentity(seedOrPrivateKey, name);
    if (imported !== null) {
      setIdentity(imported);
    }
    return imported;
  };

  const enableProtection = useCallback(
    async (password: string): Promise<void> => {
      await enableProtectionImpl(password);
      refreshIdentity();
    },
    [refreshIdentity],
  );

  const disableProtection = useCallback(
    async (password: string): Promise<void> => {
      await disableProtectionImpl(password);
      refreshIdentity();
    },
    [refreshIdentity],
  );

  const unlock = useCallback(
    async (password: string): Promise<void> => {
      await unlockImpl(password);
      refreshIdentity();
    },
    [refreshIdentity],
  );

  const lock = useCallback((): void => {
    lockImpl();
    refreshIdentity();
  }, [refreshIdentity]);

  return {
    identity,
    isLoading,
    isProtected: isProtectionEnabled(),
    isUnlocked:
      isProtectionEnabled() && getUnlockedPrivateKey() !== null,
    createNewIdentity,
    importExistingIdentity,
    enableProtection,
    disableProtection,
    unlock,
    lock,
    refreshIdentity,
  };
}
